import { useEffect } from 'react';
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';

const isDev = import.meta.env.DEV;

interface WebVitalsData {
  id: string;
  name: string;
  value: number;
  delta: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  navigationType?: string;
}

// Analytics endpoint - you can replace this with your preferred analytics service
const sendToAnalytics = (data: WebVitalsData) => {
  // Example: Send to Google Analytics 4
  if (typeof window !== 'undefined' && 'gtag' in window) {
    (window as any).gtag('event', data.name, {
      event_category: 'Web Vitals',
      event_label: data.id,
      value: Math.round(data.value),
      custom_map: {
        metric_rating: data.rating,
        metric_delta: data.delta,
      },
    });
  }

  // Note: Custom analytics endpoint disabled - configure if you have a backend API
  // To enable, uncomment and ensure /api/analytics/web-vitals endpoint exists

  // Console logging for development only
  if (isDev) {
    console.log(`[Web Vitals] ${data.name}:`, {
      value: data.value,
      rating: data.rating,
      delta: data.delta,
      id: data.id,
    });
  }
};

// Enhanced metric handler with additional context
const createMetricHandler = (metricName: string) => (metric: any) => {
  const data: WebVitalsData = {
    id: metric.id,
    name: metricName,
    value: metric.value,
    delta: metric.delta,
    rating: metric.rating,
    navigationType: metric.navigationType || 'unknown',
  };

  sendToAnalytics(data);

  // Store metrics in sessionStorage for debugging
  if (typeof window !== 'undefined') {
    const existingMetrics = JSON.parse(sessionStorage.getItem('webVitals') || '[]');
    existingMetrics.push({
      ...data,
      timestamp: Date.now(),
      url: window.location.pathname,
    });
    
    // Keep only the last 10 metrics to avoid storage bloat
    const recentMetrics = existingMetrics.slice(-10);
    sessionStorage.setItem('webVitals', JSON.stringify(recentMetrics));
  }
};

export const WebVitals = () => {
  useEffect(() => {
    // Only run in the browser and production, or if explicitly enabled in development
    if (typeof window === 'undefined') return;
    
    const shouldTrack = process.env.NODE_ENV === 'production' || 
                       localStorage.getItem('enableWebVitals') === 'true';
    
    if (!shouldTrack) return;

    try {
      // Cumulative Layout Shift - measures visual stability
      onCLS(createMetricHandler('CLS'), { reportAllChanges: true });

      // First Contentful Paint - measures when the first content is painted
      onFCP(createMetricHandler('FCP'));

      // Interaction to Next Paint - measures interaction responsiveness
      onINP(createMetricHandler('INP'), { reportAllChanges: true });

      // Largest Contentful Paint - measures loading performance
      onLCP(createMetricHandler('LCP'), { reportAllChanges: true });

      // Time to First Byte - measures server response time
      onTTFB(createMetricHandler('TTFB'));

      if (isDev) console.log('[Web Vitals] Monitoring initialized');

    } catch (error) {
      if (isDev) console.warn('[Web Vitals] Failed to initialize monitoring:', error);
    }
  }, []);

  return null; // This component doesn't render anything
};

// Utility function to get current web vitals from sessionStorage
export const getCurrentWebVitals = () => {
  if (typeof window === 'undefined') return [];
  return JSON.parse(sessionStorage.getItem('webVitals') || '[]');
};

// Utility function to enable web vitals tracking in development
export const enableDevWebVitals = () => {
  localStorage.setItem('enableWebVitals', 'true');
  window.location.reload();
};

// Utility function to disable web vitals tracking
export const disableWebVitals = () => {
  localStorage.removeItem('enableWebVitals');
  sessionStorage.removeItem('webVitals');
};

// Performance thresholds based on Google's recommendations
export const PERFORMANCE_THRESHOLDS = {
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  INP: { good: 200, poor: 500 },
  LCP: { good: 2500, poor: 4000 },
  TTFB: { good: 800, poor: 1800 },
};

// Utility function to get performance summary
export const getPerformanceSummary = () => {
  const metrics = getCurrentWebVitals();
  const summary: Record<string, any> = {};

  metrics.forEach((metric: any) => {
    if (!summary[metric.name]) {
      summary[metric.name] = {
        latest: metric,
        count: 0,
        ratings: { good: 0, 'needs-improvement': 0, poor: 0 },
      };
    }
    
    summary[metric.name].count++;
    summary[metric.name].ratings[metric.rating]++;
    
    // Keep the latest metric
    if (metric.timestamp > (summary[metric.name].latest.timestamp || 0)) {
      summary[metric.name].latest = metric;
    }
  });

  return summary;
};

export default WebVitals;